import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { postLoginPath } from '../../utils/roleLanding';
import CreateInstitution from '../institutions/CreateInstitution';
import TwoFactorVerify from './TwoFactorVerify';
import TwoFactorEnroll from './TwoFactorEnroll';
import {
    Alert,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Divider,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface LoginFormProps {
    onLoginSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showCreateInstitutionModal, setShowCreateInstitutionModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [challenge, setChallenge] = useState<{ type: 'OTP' | 'ENROLLMENT'; challengeId: string; qrDataUri?: string; secret?: string } | null>(null);
    const { login } = useAuth();
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const location = useLocation();
    // A deep link the user was bounced from wins (route guards' state.from, or ?from=); otherwise
    // land by role on /home. Only a same-origin app path survives (no open redirect, never /login).
    const afterLogin = postLoginPath(location.state, location.search);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await login({ username, password });

            if (result.success) {
                if (onLoginSuccess) {
                    onLoginSuccess();
                }
                navigate(afterLogin);
            } else if (result.requiresTwoFactor && result.challengeId) {
                // For ENROLLMENT challenges, validate that qrDataUri and secret are present
                if (result.requiresTwoFactor === 'ENROLLMENT' && (!result.qrDataUri || !result.secret)) {
                    setError(t('loginForm.enrollmentStartFailed'));
                } else {
                    setChallenge({
                        type: result.requiresTwoFactor,
                        challengeId: result.challengeId,
                        qrDataUri: result.qrDataUri,
                        secret: result.secret,
                    });
                }
            } else {
                setError(result.error || t('loginForm.loginFailed'));
            }
        } catch (err: any) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(err.response?.data?.message || t('loginForm.invalidCredentials'));
            }
            console.error('Login error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateInstitutionSuccess = (message?: string, email?: string) => {
        setShowCreateInstitutionModal(false);
        let successMsg = message || t('loginForm.institutionCreated');
        if (email) {
            successMsg += ` ${t('loginForm.credentialsSentTo', { email })}`;
        }
        setSuccessMessage(successMsg);
    };

    if (challenge?.type === 'OTP') {
        return (
            <TwoFactorVerify
                challengeId={challenge.challengeId}
                onSuccess={() => { if (onLoginSuccess) onLoginSuccess(); navigate(afterLogin); }}
                onCancel={() => setChallenge(null)}
            />
        );
    }

    if (challenge?.type === 'ENROLLMENT' && challenge.qrDataUri && challenge.secret) {
        return (
            <TwoFactorEnroll
                challengeId={challenge.challengeId}
                qrDataUri={challenge.qrDataUri}
                secret={challenge.secret}
                onSuccess={() => { if (onLoginSuccess) onLoginSuccess(); navigate(afterLogin); }}
                onCancel={() => setChallenge(null)}
            />
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} mb={2}>
                {t('loginForm.title')}
            </Typography>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <TextField
                    label={t('loginForm.username')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder={t('loginForm.usernamePlaceholder')}
                />
                <TextField
                    label={t('loginForm.password')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder={t('loginForm.passwordPlaceholder')}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                        aria-label={showPassword ? t('loginForm.hidePassword') : t('loginForm.showPassword')}
                                    >
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                        backgroundColor: '#10b981',
                        '&:hover': { backgroundColor: '#0ea271' },
                        textTransform: 'none',
                        py: 1,
                    }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('loginForm.submit')}
                </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" mb={1}>
                    {t('loginForm.noInstitution')}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => setShowCreateInstitutionModal(true)}
                    sx={{
                        backgroundColor: '#28a745',
                        '&:hover': { backgroundColor: '#218838' },
                        textTransform: 'none',
                    }}
                >
                    {t('loginForm.createInstitution')}
                </Button>
            </Box>

            <CreateInstitution
                isModal={true}
                open={showCreateInstitutionModal}
                onClose={() => setShowCreateInstitutionModal(false)}
                onSuccess={handleCreateInstitutionSuccess}
            />
        </Box>
    );
};

export default LoginForm;
